import React, { useState, useEffect } from "react";

const parseRegexPattern = (pattern) => {
    const result = {
        lowercase: false,
        uppercase: false,
        digits: false,
        useExact: true,
        exactLength: 12,
        minLength: 6,
        maxLength: 12,
    };

    const match = pattern.match(/^\^?\[([a-zA-Z0-9\\\-]+)\]\{(\d+)(,(\d+))?\}\$$/);
    if (!match) return result;

    const charset = match[1];
    const min = parseInt(match[2]);
    const max = match[4] ? parseInt(match[4]) : null;

    result.lowercase = charset.includes("a-z");
    result.uppercase = charset.includes("A-Z");
    result.digits = charset.includes("0-9");

    if (max !== null) {
        result.useExact = false;
        result.minLength = min;
        result.maxLength = max;
    } else {
        result.useExact = true;
        result.exactLength = min;
    }

    return result;
};

const generateRegex = ({
    lowercase,
    uppercase,
    digits,
    useExact,
    exactLength,
    minLength,
    maxLength,
}) => {
    let charset = "";
    if (lowercase) charset += "a-z";
    if (uppercase) charset += "A-Z";
    if (digits) charset += "0-9";
    if (!charset) return "";

    const quantifier = useExact
        ? `{${exactLength}}`
        : `{${minLength},${maxLength}}`;

    return `^[${charset}]${quantifier}$`;
};

const RegexInput = ({ value = "", onChange }) => {
    const initial = parseRegexPattern(value);
    // console.log({ value, initial })

    const [lowercase, setLowercase] = useState(initial.lowercase);
    const [uppercase, setUppercase] = useState(initial.uppercase);
    const [digits, setDigits] = useState(initial.digits);
    const [useExact, setUseExact] = useState(initial.useExact);
    const [exactLength, setExactLength] = useState(initial.exactLength);
    const [minLength, setMinLength] = useState(initial.minLength);
    const [maxLength, setMaxLength] = useState(initial.maxLength);
    const [regex, setRegex] = useState("");
    const [testInput, setTestInput] = useState("");

    // useEffect(() => {
    //     console.log({ value })
    // }, [value])

    useEffect(() => {
        const pattern = generateRegex({
            lowercase,
            uppercase,
            digits,
            useExact,
            exactLength,
            minLength,
            maxLength,
        });
        setRegex(pattern);
        onChange?.(pattern);
    }, [lowercase, uppercase, digits, useExact, exactLength, minLength, maxLength]);

    const isMatch = () => {
        try {
            const re = new RegExp(regex);
            return re.test(testInput);
        } catch {
            return false;
        }
    };

    return (
        <div className="container p-3 border rounded">
            <h6 className="form-label mb-3">Regex Generator</h6>

            <div className="form-check form-check-inline">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={lowercase}
                    onChange={(e) => setLowercase(e.target.checked)}
                    id="lowercase"
                />
                <label className="form-check-label" htmlFor="lowercase">
                    Lowercase (a-z)
                </label>
            </div>

            <div className="form-check form-check-inline">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={uppercase}
                    onChange={(e) => setUppercase(e.target.checked)}
                    id="uppercase"
                />
                <label className="form-check-label" htmlFor="uppercase">
                    Uppercase (A-Z)
                </label>
            </div>

            <div className="form-check form-check-inline mb-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={digits}
                    onChange={(e) => setDigits(e.target.checked)}
                    id="digits"
                />
                <label className="form-check-label" htmlFor="digits">
                    Digits (0-9)
                </label>
            </div>

            <div className="mb-3">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={useExact}
                        onChange={() => setUseExact(true)}
                        id="exact"
                        name="length"
                    />
                    <label className="form-check-label" htmlFor="exact">
                        Exact Length
                    </label>
                </div>
                {useExact && (
                    <input
                        type="number"
                        className="form-control w-25 mt-1"
                        value={exactLength}
                        onChange={(e) => setExactLength(Number(e.target.value))}
                    />
                )}
            </div>

            <div className="mb-3">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={!useExact}
                        onChange={() => setUseExact(false)}
                        id="range"
                        name="length"
                    />
                    <label className="form-check-label" htmlFor="range">
                        Min/Max Length
                    </label>
                </div>
                {!useExact && (
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <input
                            type="number"
                            className="form-control w-25"
                            value={minLength}
                            onChange={(e) => setMinLength(Number(e.target.value))}
                        />
                        <span>to</span>
                        <input
                            type="number"
                            className="form-control w-25"
                            value={maxLength}
                            onChange={(e) => setMaxLength(Number(e.target.value))}
                        />
                    </div>
                )}
            </div>

            <div className="mt-4">
                <label className="form-label">Test input</label>
                <input
                    type="text"
                    className="form-control"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                />
                <div className={`mt-2 ${isMatch() ? "text-success" : "text-danger"}`}>
                    {testInput.length === 0
                        ? "Enter a value to test"
                        : isMatch()
                            ? "✅ Match"
                            : "❌ No match"}
                </div>
            </div>

            <div className="mt-3">
                <strong>Generated Regex:</strong>
                <code className="d-block bg-light p-2 border rounded mt-1">{regex}</code>
            </div>
        </div>
    );
};

export default RegexInput;
